/*
 * Drag lifecycle monitor + stuck-drag watchdog.
 *
 * Background: an HTML5 drag puts Chrome into a browser-wide modal drag loop
 * that only exits when the mouse release (or Esc) is delivered. Remote
 * desktop tools (AnyDesk etc.) can lose that release, leaving the whole
 * browser ignoring clicks while wheel scrolling still works — reported by an
 * SFP user as "dragging a document freezes Chrome" (Sep 2026). Locally the
 * drag pipeline always measured healthy (dragend fired 7/7, handlers < 71ms),
 * so this module exists to (a) tell the user how to recover and (b) capture
 * evidence when it happens in the field.
 *
 * What it does:
 *  - logs every drag lifecycle transition (start/end/drop/enter/leave) with
 *    timing, coordinates and source-element state into a ring buffer
 *  - summarises high-frequency dragover/drag activity per second instead of
 *    per event, so the log stays readable and cheap
 *  - watchdog: if a drag goes silent (no dragover/drag heartbeat) without a
 *    dragend/drop, it logs a STALL and shows a snack telling the user to
 *    press Esc
 *
 * Reading the evidence on a client machine (F12 console):
 *  - live entries are tagged [DragMonitor]
 *  - window.__dragLog            -> the ring buffer (newest last)
 *  - window.__dragLogDump()      -> JSON string of the buffer for copy/paste
 */

const snack = require('@/snack');

const MAX_LOG = 400;
const HEARTBEAT_CHECK_MS = 1000;
const STALL_AFTER_MS = 3000;   // no heartbeat for this long => stalled
const HARD_STUCK_AFTER_MS = 15000;

const log = [];
let seq = 0;
let session = null;   // the active drag, null when no drag is open
let watchdog = null;

function now() { return Date.now(); }

function describeEl(el) {
    if (!el || !el.tagName) return null;
    return {
        tag: el.tagName,
        cls: String(el.className && el.className.baseVal !== undefined ? el.className.baseVal : el.className || '').slice(0, 80),
        connected: el.isConnected
    };
}

function push(type, detail, echo = false) {
    const entry = {n: ++seq, t: new Date().toISOString(), type, ...detail};
    log.push(entry);
    if (log.length > MAX_LOG) log.shift();
    if (echo)
        // eslint-disable-next-line
        console.info('[DragMonitor]', type, entry);
    return entry;
}

function flushHeartbeat() {
    if (session && session.pendingMoves > 0) {
        push('heartbeat', {
            dragId: session.id,
            moves: session.pendingMoves,
            lastXY: session.lastXY,
            overTarget: session.lastOverTarget
        });
        session.pendingMoves = 0;
    }
}

function endSession(how, ev) {
    if (!session) {
        // dragend/drop with no open session is itself worth knowing about
        push(`${how}-without-start`, {target: describeEl(ev && ev.target)}, true);
        return;
    }
    flushHeartbeat();
    push(how, {
        dragId: session.id,
        durationMs: now() - session.startedAt,
        totalMoves: session.totalMoves,
        stalls: session.stalls,
        sourceConnected: session.sourceEl ? session.sourceEl.isConnected : null,
        target: describeEl(ev && ev.target),
        lastXY: session.lastXY
    }, true);
    session = null;
    if (watchdog) { clearInterval(watchdog); watchdog = null; }
}

function onDragStart(ev) {
    // A second dragstart while a session is open means the previous drag
    // never delivered its dragend — exactly the stuck case. Log loudly.
    if (session)
        push('start-while-open', {previousDragId: session.id, openForMs: now() - session.startedAt}, true);

    session = {
        id: ++seq,
        startedAt: now(),
        lastHeartbeat: now(),
        totalMoves: 0,
        pendingMoves: 0,
        stalls: 0,
        stallNotified: false,
        sourceEl: ev.target,
        lastXY: {x: ev.clientX, y: ev.clientY},
        lastOverTarget: null
    };
    push('dragstart', {
        dragId: session.id,
        source: describeEl(ev.target),
        xy: session.lastXY,
        types: ev.dataTransfer ? [...(ev.dataTransfer.types || [])] : null
    }, true);

    // Smoking-gun probe: Chrome silently kills a drag (no dragover, no
    // dragend, input state can wedge) when the source node is detached while
    // the drag is being established. A Vue re-render queued by a dragstart
    // handler flushes on the MICROTASK queue — before the browser continues —
    // so check connectivity right then, and again shortly after.
    const src = ev.target, id = session.id;
    Promise.resolve().then(() => {
        if (!src.isConnected) push('source-detached-at-microtask', {dragId: id}, true);
    });
    setTimeout(() => {
        if (session && session.id === id && !src.isConnected)
            push('source-detached-at-50ms', {dragId: id}, true);
    }, 50);

    if (watchdog) clearInterval(watchdog);
    watchdog = setInterval(() => {
        if (!session) { clearInterval(watchdog); watchdog = null; return; }
        flushHeartbeat();
        const silentFor = now() - session.lastHeartbeat;
        if (silentFor > STALL_AFTER_MS) {
            session.stalls++;
            // keep logging input evidence for a while after this drag ends
            postMortemUntil = now() + 120000;
            push('STALL', {
                dragId: session.id,
                silentForMs: silentFor,
                openForMs: now() - session.startedAt,
                sourceConnected: session.sourceEl ? session.sourceEl.isConnected : null,
                lastXY: session.lastXY
            }, true);
            // One friendly hint per drag; a silent period is also normal when
            // the cursor left the browser window, so phrase it as a maybe.
            if (!session.stallNotified) {
                session.stallNotified = true;
                snack.info('If clicks stop responding after dragging: press Esc, then click once. Over remote desktop, also try Alt+Tab away and back');
            }
            if (silentFor > HARD_STUCK_AFTER_MS) {
                push('STUCK-HARD', {dragId: session.id, silentForMs: silentFor}, true);
                clearInterval(watchdog);
                watchdog = null;
            }
        }
    }, HEARTBEAT_CHECK_MS);
}

function onMove(ev) {
    if (!session) return;
    session.lastHeartbeat = now();
    session.totalMoves++;
    session.pendingMoves++;
    session.lastXY = {x: ev.clientX, y: ev.clientY};
    if (ev.type === 'dragover') {
        const d = describeEl(ev.target);
        session.lastOverTarget = d ? `${d.tag}.${d.cls.split(' ')[0] || ''}` : null;
    }
}

function onKeyDown(ev) {
    if (ev.key === 'Escape' && (session || inPostMortem()))
        push('escape-pressed', {dragId: session ? session.id : null, duringSession: !!session}, true);
}

/*
 * Input-path evidence (added after the 2026-09-14 AnyDesk incident where the
 * app stayed click-dead and even Esc did not recover it). The open question
 * there: do pointer events still REACH the page (an in-page click-eater) or
 * does input stop arriving entirely (remote-desktop mouse-button desync at OS
 * level, where the host believes the button is still held)? Logging pointer
 * events while a drag session is open — and for a post-mortem window after a
 * stall — answers that:
 *  - pointer events logged with buttons=1 while nothing is pressed remotely
 *    => host thinks the button is stuck down (AnyDesk lost the mouse-up)
 *  - clicks logged but the UI ignores them  => in-page problem, investigate app
 *  - nothing logged at all                  => input never reaches the page
 */
let postMortemUntil = 0;
function inPostMortem() { return now() < postMortemUntil; }
let lastMoveSample = 0;

function onPointerEvidence(ev) {
    const relevant = session || inPostMortem();
    if (!relevant) return;
    if (ev.type === 'mousemove' || ev.type === 'pointermove') {
        // sampled: one entry per second is plenty to show buttons state
        if (now() - lastMoveSample < 1000) return;
        lastMoveSample = now();
    }
    push(`input-${ev.type}`, {
        dragId: session ? session.id : null,
        xy: {x: ev.clientX, y: ev.clientY},
        buttons: ev.buttons,
        target: describeEl(ev.target)
    }, true);
    // any pointer press/release while a drag session is open means the input
    // path is alive even though the drag went silent — log the contrast loudly
    if (session && (ev.type === 'mousedown' || ev.type === 'mouseup'))
        push('input-alive-during-stuck-drag', {dragId: session.id, openForMs: now() - session.startedAt}, true);
}

function installDragMonitor() {
    if (window.__dragMonitorInstalled) return;
    window.__dragMonitorInstalled = true;

    document.addEventListener('dragstart', onDragStart, true);
    document.addEventListener('drag', onMove, true);
    document.addEventListener('dragover', onMove, true);
    document.addEventListener('dragenter', ev => {
        if (session) session.lastHeartbeat = now();
        // dragenter with files and no session = an OS file drag entering the window
        else if (ev.dataTransfer && [...(ev.dataTransfer.types || [])].includes('Files'))
            push('os-file-drag-entered', {target: describeEl(ev.target)}, true);
    }, true);
    document.addEventListener('dragend', ev => endSession('dragend', ev), true);
    document.addEventListener('drop', ev => endSession('drop', ev), true);
    document.addEventListener('keydown', onKeyDown, true);
    ['mousedown', 'mouseup', 'click', 'mousemove'].forEach(t =>
        document.addEventListener(t, onPointerEvidence, true));

    window.__dragLog = log;
    window.__dragLogDump = () => JSON.stringify(log, null, 1);
    window.__dragStatus = () => ({
        sessionOpen: !!session,
        dragId: session ? session.id : null,
        openForMs: session ? now() - session.startedAt : null,
        stalls: session ? session.stalls : null,
        postMortem: inPostMortem(),
        entries: log.length
    });

    push('monitor-installed', {ua: navigator.userAgent.slice(0, 120)}, true);
}

module.exports = {installDragMonitor};
